import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { EDUCATION_FAQ_CATEGORIES } from "@/content/education-corpus/faq-defs";

export const Route = createFileRoute("/education/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/education-corpus");
    return { faqs: m.EDUCATION_FAQ_TH };
  },
  component: EducationFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `รับรองเอกสารการศึกษา — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องรับรองปริญญาบัตรและทรานสคริปต์ นิติกรณ์กรมการกงสุล แปลรับรองเอกสารการศึกษา Apostille (ไทยเริ่ม 28 ก.พ. 2570) ประเมินเทียบวุฒิ WES/ENIC-NARIC ใบอนุญาตครูคุรุสภา เทียบวุฒิในไทย และรับรองที่สถานทูตปลายทาง",
        path: "/education/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/education/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "เอกสารการศึกษา FAQ", url: `${site}/education/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/education/faq")) },
      ],
    };
  },
});

function EducationFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>เอกสารการศึกษา FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          รับรองเอกสารการศึกษา วุฒิการศึกษา และทรานสคริปต์ — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p data-speakable="true" className="speakable-summary mb-6 text-muted-foreground">
          คำตอบอ้างอิงแนวปฏิบัติของกองสัญชาติและนิติกรณ์ กรมการกงสุล (consular.mfa.go.th) กระทรวงศึกษาธิการ (moe.go.th)
          กระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม (mhesi.go.th) คุรุสภา (ksp.or.th) และอนุสัญญา Apostille ของ HCCH
          ซึ่งจะมีผลใช้บังคับกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 ข้อมูลนี้เป็นข้อมูลทั่วไป ไม่ใช่คำวินิจฉัยของหน่วยงานราชการ
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/education/faq" className="text-primary underline">Education document certification FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {EDUCATION_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/education/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <ServiceAdviceBlock
          locale="th"
          items={[
            "ขอ “ใบรับรองผลการศึกษา (Transcript)” และปริญญาบัตรฉบับภาษาอังกฤษจากสถาบันเดิมก่อน เพราะกองสัญชาติและนิติกรณ์ กรมการกงสุล รับรองเอกสารที่ออกโดยหน่วยงานที่มีอำนาจ และเอกสารภาษาอังกฤษต้นฉบับช่วยลดขั้นตอนแปล",
            "ถ้าเอกสารเป็นภาษาไทย ต้องแปลเป็นภาษาอังกฤษให้ตรงตัวสะกดชื่อ-นามสกุลกับหนังสือเดินทาง แล้วยื่นรับรองคำแปลที่กรมการกงสุล (ถนนแจ้งวัฒนะ หรือสำนักงานสาขา) พร้อมต้นฉบับทุกครั้ง",
            "สำหรับวุฒิที่ออกจากต่างประเทศและจะใช้ในไทย ให้รับรองที่หน่วยงานของประเทศต้นทางก่อน แล้วรับรองที่สถานทูต/สถานกงสุลไทย หรือใช้ Apostille เมื่ออนุสัญญามีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570",
            "หากต้องการเทียบวุฒิเพื่อเรียนต่อหรือสมัครงานในไทย ให้ตรวจแนวทางของกระทรวงศึกษาธิการ (moe.go.th) และกระทรวง อว. (mhesi.go.th) ว่าต้องใช้ใบเทียบระดับการศึกษาหรือไม่ ก่อนเริ่มขั้นตอนรับรอง",
            "งานสอนในไทยต้องดูข้อกำหนดใบอนุญาตประกอบวิชาชีพครูของคุรุสภา (ksp.or.th) ควบคู่กับใบอนุญาตทำงาน เพราะเอกสารที่ต้องรับรองจะต่างจากการสมัครเรียนต่อ",
            "สอบถามรายการเอกสาร ขั้นตอน และกรอบเวลาที่แน่นอนกับเจ้าหน้าที่ทางโทรศัพท์ LINE หรืออีเมลก่อนส่งเอกสาร เพราะข้อกำหนดของแต่ละสถาบันและแต่ละประเทศปลายทางไม่เหมือนกัน",
          ]}
        />
        <ServiceCautionBlock
          locale="th"
          items={[
            "ยื่นเฉพาะสำเนาหรือไฟล์สแกนโดยไม่มีต้นฉบับ — งานนิติกรณ์ต้องตรวจต้นฉบับหรือสำเนาที่สถาบันรับรองความถูกต้อง",
            "ชื่อ-นามสกุลในคำแปลสะกดไม่ตรงกับหนังสือเดินทางหรือทะเบียนราษฎร ทำให้หน่วยงานปลายทางปฏิเสธ",
            "ทรานสคริปต์ที่สถาบันออกให้แบบ “ไม่ปิดผนึก” หรือสำเนาที่นักศึกษาพิมพ์เอง มักไม่ผ่านการประเมินเทียบวุฒิของหน่วยงานต่างประเทศ",
            "เข้าใจว่า Apostille ใช้ได้ทันที — ปัจจุบันไทยยังใช้นิติกรณ์กรมการกงสุลและรับรองสถานทูตปลายทาง จนกว่าอนุสัญญาจะมีผลวันที่ 28 กุมภาพันธ์ 2570",
            "รับรองเอกสารเพียงชั้นเดียวแล้วส่งไปต่างประเทศ โดยไม่ได้ตรวจว่าประเทศปลายทางต้องการรับรองที่สถานทูตของประเทศนั้นในไทยเพิ่มอีกชั้น",
            "วางแผนกระชั้นเกินไปก่อนกำหนดปิดรับสมัครเรียนหรือวันเริ่มงาน โดยไม่เผื่อเวลาสำหรับการออกเอกสารใหม่จากสถาบัน",
          ]}
        />
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li><a className="text-primary underline" href="/translation/faq">แปลเอกสารรับรอง</a></li>
            <li><a className="text-primary underline" href="/consular-legalization/faq">นิติกรณ์กรมการกงสุล / Apostille</a></li>
            <li><a className="text-primary underline" href="/notary/faq">Notary Public / หนังสือมอบอำนาจ</a></li>
            <li><a className="text-primary underline" href="/work-permit/faq">ใบอนุญาตทำงาน</a></li>
          </ul>
        </section>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายการเอกสาร ขั้นตอน และกรอบเวลาได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="education" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}